import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import {
  api,
  type SourceRepo,
  type SourceRepoStatus,
  type KnowledgeUpdateRepository,
} from "../api/client";
import "../components/knowledge-update-dialog";
import { session } from "../auth/session";
import { i18n } from "../i18n";
import { dateTime } from "../view-model";
import { pageCount } from "../components/app-pagination";
import "../components/app-dialog";
import { TableColumns } from "../components/table-columns";

@customElement("source-repos-view")
export class SourceReposView extends LitElement {
  private readonly columns = new TableColumns(this);
  @state() private data: SourceRepoStatus | null = null;
  @state() private selected = new Set<string>();
  @state() private updateRepositories: KnowledgeUpdateRepository[] = [];
  @state() private updateOpen = false;
  private knownRepos = new Map<string, SourceRepo>();
  @state() private detailId = "";
  @state() private page = 1;
  @state() private pageSize = 10;
  @state() private errorRepoName = "";
  @state() private errorScope = "";
  @state() private editor: Pick<
    SourceRepo,
    "name" | "url" | "branch" | "auto_refresh" | "copy_enabled"
  > | null = null;
  @state() private editName = "";
  @state() private deleteRepo: SourceRepo | null = null;
  @state() private actionError = "";
  @state() private loading = true;
  @state() private busy = false;
  @state() private error = "";
  @state() private message = "";
  private timer = 0;
  private fetching = false;

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
    void this.load();
    this.timer = window.setInterval(() => {
      if (!document.hidden) void this.load();
    }, 3000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  private languageChanged = () => this.requestUpdate();

  private get errorRepo(): SourceRepo | null {
    if (!this.errorRepoName) return null;
    return (
      (this.errorScope === "current"
        ? this.data?.items.find((repo) => repo.name === this.errorRepoName)
        : this.data?.history.find((run) => run.id === this.errorScope)
            ?.repositories[this.errorRepoName]) || null
    );
  }

  private async load() {
    if (this.fetching) return;
    this.fetching = true;
    try {
      this.data = await api.sourceRepos(this.page, this.pageSize);
      for (const repo of this.data.items) this.knownRepos.set(repo.name, repo);
      if (this.updateOpen) {
        const repositories = this.updateRepositories.map((selection) => {
          const repo = this.knownRepos.get(selection.id);
          return {
            ...selection,
            ready: repo?.ready === true && repo.copy_enabled !== false,
          };
        });
        if (
          JSON.stringify(repositories) !==
          JSON.stringify(this.updateRepositories)
        )
          this.updateRepositories = repositories;
      }
      this.page = this.data.page;
      this.error = "";
    } catch (error) {
      this.error = (error as Error).message;
    } finally {
      this.fetching = false;
      this.loading = false;
    }
  }

  private async refreshRepos(repositories?: string[]) {
    this.busy = true;
    this.actionError = "";
    this.message = "";
    try {
      await api.refreshSources(repositories);
      this.message = i18n.t("sourceAccepted");
      await this.load();
    } catch (error) {
      this.actionError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private openEditor(repo?: SourceRepo) {
    this.editName = repo?.name || "";
    this.editor = repo
      ? {
          name: repo.name,
          url: repo.url,
          branch: repo.branch,
          auto_refresh: repo.auto_refresh,
          copy_enabled: repo.copy_enabled,
        }
      : {
          name: "",
          url: "https://gitcode.com/",
          branch: "master",
          auto_refresh: true,
          copy_enabled: true,
        };
    this.actionError = "";
  }

  private async saveRepo() {
    if (!this.editor) return;
    this.busy = true;
    this.actionError = "";
    try {
      const value = this.editor;
      const old = this.data?.items.find((repo) => repo.name === this.editName);
      const initialize =
        !old ||
        old.url !== value.url ||
        old.branch !== value.branch ||
        !old.ready;
      await api.saveSourceRepo(value, this.editName || undefined);
      this.editor = null;
      this.message = i18n.t("sourceSaved");
      if (
        initialize &&
        !old?.updating &&
        (value.auto_refresh || value.copy_enabled)
      )
        await api.refreshSources([value.name]);
      await this.load();
    } catch (error) {
      this.actionError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private async removeRepo() {
    if (!this.deleteRepo) return;
    this.busy = true;
    this.actionError = "";
    try {
      await api.deleteSourceRepo(this.deleteRepo.name);
      this.selected = new Set(
        [...this.selected].filter((name) => name !== this.deleteRepo?.name),
      );
      this.deleteRepo = null;
      await this.load();
    } catch (error) {
      this.actionError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private async stopRun(id: string) {
    this.busy = true;
    this.actionError = "";
    try {
      await api.stopSourceRun(id);
      await this.load();
    } catch (error) {
      this.actionError = (error as Error).message;
    } finally {
      this.busy = false;
    }
  }

  private showRepo(repo: SourceRepo, scope: string) {
    this.errorRepoName = repo.name;
    this.errorScope = scope;
  }

  private detailButton(repo: SourceRepo, scope = "current") {
    return html`<a2k-button
      size="small"
      @click=${() => this.showRepo(repo, scope)}
    >
      ${i18n.t("details")}</a2k-button
    >`;
  }

  private select(name: string, checked: boolean) {
    const selected = new Set(this.selected);
    if (checked) selected.add(name);
    else selected.delete(name);
    this.selected = selected;
  }
  private selectPage(checked: boolean) {
    for (const repo of this.data?.items || []) this.select(repo.name, checked);
  }
  private knowledgeSelection(): KnowledgeUpdateRepository[] {
    return [...this.selected].map((name) => {
      const repo =
        this.data?.items.find((item) => item.name === name) ||
        this.knownRepos.get(name);
      return {
        id: name,
        label: name,
        ready: repo?.ready === true && repo.copy_enabled !== false,
      };
    });
  }
  private openKnowledgeUpdate() {
    if (session.user?.role !== "administrator" || !this.selected.size) return;
    this.updateRepositories = this.knowledgeSelection();
    this.updateOpen = true;
  }

  private phase(value?: string) {
    switch (value) {
      case "success":
        return i18n.t("sourceSuccess");
      case "failed":
        return i18n.t("sourceFailed");
      case "running":
        return i18n.t("sourceRunning");
      case "retrying":
        return i18n.t("sourceRetrying");
      case "interrupted":
        return i18n.t("sourceInterrupted");
      case "skipped":
        return i18n.t("sourceSkipped");
      case "stopped":
        return i18n.t("sourceStopped");
      default:
        return i18n.t("sourcePending");
    }
  }

  private duration(start?: string, end?: string) {
    if (!start) return "—";
    const seconds = Math.max(
      0,
      Math.round(
        ((end ? Date.parse(end) : Date.now()) - Date.parse(start)) / 1000,
      ),
    );
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  }

  private errors(repo: SourceRepo) {
    return html`
      ${repo.error ? html`<span class="status-error">${repo.error}</span>` : "—"}
      ${
        repo.attempt_errors?.length
          ? html`<details>
              <summary>
                ${i18n.t("sourceError")} (${repo.attempt_errors.length})
              </summary>
              ${repo.attempt_errors.map(
                (entry) =>
                  html`<p>
                    #${entry.attempt} · ${dateTime(entry.at)} · ${entry.error}
                  </p>`,
              )}
            </details>`
          : null
      }
      ${
        repo.filesystem_omissions?.length
          ? html`<details>
              <summary>
                ${i18n.t("sourceOmissions")}
                (${repo.filesystem_omissions.length})
              </summary>
              ${repo.filesystem_omissions.map((path) => html`<p><code>${path}</code></p>`)}
            </details>`
          : null
      }
    `;
  }

  private triggerLabel(trigger: string) {
    return i18n.t(
      ["hourly", "scheduled", "initialization"].includes(trigger)
        ? "sourceHourly"
        : "sourceManual",
    );
  }

  private nextUpdate(repo: SourceRepo) {
    return dateTime(
      repo.auto_refresh && this.data?.schedule?.enabled
        ? this.data.schedule.next_run
        : null,
    );
  }

  render() {
    const repos = this.data?.items || [];
    const history = this.data?.history || [];
    const detail = history.find((run) => run.id === this.detailId);
    const disabled = this.busy || session.user?.role === "viewer";
    const selectedAvailable = repos.some(
      (repo) => this.selected.has(repo.name) && !repo.updating,
    );
    const anyAvailable = repos.some((repo) => !repo.updating);
    const admin = session.user?.role === "administrator";
    const knowledgeReady =
      this.selected.size > 0 &&
      this.knowledgeSelection().every((repo) => repo.ready);
    return html`
      <page-heading
        heading=${i18n.t("sources")}
        description=${i18n.t("sourceDescription")}
      >
        <a2k-button
          ?disabled=${disabled || !selectedAvailable}
          @click=${() => this.refreshRepos([...this.selected])}
        >
          ${i18n.t("sourceSelected", { count: this.selected.size })}
        </a2k-button>
        <a2k-button
          ?disabled=${disabled || !anyAvailable}
          @click=${() => this.refreshRepos()}
          >${i18n.t("sourceAll")}</a2k-button
        >
        <a2k-button
          ?disabled=${!admin || this.busy || !this.selected.size}
          @click=${() => this.openKnowledgeUpdate()}
          >${i18n.t("knowledgeUpdateSelected", { count: this.selected.size })}</a2k-button
        >
        ${
          admin
            ? html`<a2k-button
                ?disabled=${this.busy}
                @click=${() => this.openEditor()}
                >${i18n.t("sourceAdd")}</a2k-button
              >`
            : null
        }
      </page-heading>
      ${
        !admin
          ? html`<p class="status">${i18n.t("knowledgeUpdateAdminOnly")}</p>`
          : this.selected.size && !knowledgeReady
            ? html`<p class="status">${i18n.t("knowledgeUpdateNotReady")}</p>`
            : null
      }
      ${this.error ? html`<p class="status status-error" role="alert">${this.error}</p>` : null}
      ${this.actionError ? html`<p class="status status-error" role="alert">${this.actionError}</p>` : null}
      ${this.message ? html`<p class="status status-success">${this.message}</p>` : null}
      <p class="status">
        <a href="/console/jobs">${i18n.t("sourceScheduleLink")}</a>
      </p>
      ${this.data && !this.data.schedule?.enabled ? html`<p class="status">${i18n.t("sourceAutoOff")}</p>` : null}
      ${
        this.loading
          ? html`<a2k-progress></a2k-progress>`
          : html`
              <surface-window heading=${i18n.t("sources")}>
                <div class="table-wrap source-table-wrap">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            aria-label=${i18n.t("selectAll")}
                            .checked=${repos.length > 0 && repos.every((repo) => this.selected.has(repo.name))}
                            .indeterminate=${
                              repos.some((repo) =>
                                this.selected.has(repo.name),
                              ) &&
                              !repos.every((repo) =>
                                this.selected.has(repo.name),
                              )
                            }
                            @change=${(event: Event) => {
                              this.selectPage(
                                (event.target as HTMLInputElement).checked,
                              );
                            }}
                          />
                        </th>
                        <th>${i18n.t("name")}</th>
                        <th>${i18n.t("sourceBranch")}</th>
                        <th>${i18n.t("sourceAuto")}</th>
                        <th>${i18n.t("sourceCopy")}</th>
                        <th>${i18n.t("state")}</th>
                        <th>${i18n.t("sourceLatest")}</th>
                        <th>${i18n.t("sourceLast")}</th>
                        <th>${i18n.t("sourceNext")}</th>
                        <th>${i18n.t("sourceResult")}</th>
                        <th>${i18n.t("sourceCommit")}</th>
                        <th>${i18n.t("details")}</th>
                        <th class="source-actions-column">
                          ${i18n.t("actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      ${repos.map(
                        (repo) =>
                          html`<tr>
                            <td>
                              <input
                                type="checkbox"
                                aria-label=${repo.name}
                                .checked=${this.selected.has(repo.name)}
                                @change=${(event: Event) => {
                                  this.select(
                                    repo.name,
                                    (event.target as HTMLInputElement).checked,
                                  );
                                }}
                              />
                            </td>
                            <td>
                              <a
                                href=${repo.url.replace(/\.git$/, "")}
                                target="_blank"
                                rel="noopener"
                                >${repo.name}</a
                              >
                            </td>
                            <td>${repo.branch}</td>
                            <td>
                              ${i18n.t(repo.auto_refresh ? "enabled" : "disabled")}
                            </td>
                            <td>
                              ${i18n.t(repo.copy_enabled ? "enabled" : "disabled")}
                            </td>
                            <td>
                              ${
                                repo.updating
                                  ? html`<a2k-button
                                      size="small"
                                      @click=${() => this.showRepo(repo, "current")}
                                      >${this.phase(repo.phase)}
                                      ${repo.attempt ? html`#${repo.attempt}` : ""}</a2k-button
                                    >`
                                  : this.phase(repo.phase)
                              }
                            </td>
                            <td>
                              ${i18n.t(
                                repo.freshness === "verified"
                                  ? "sourceVerified"
                                  : repo.freshness === "stale"
                                    ? "sourceStale"
                                    : "sourceUnknown",
                              )}
                              ${
                                repo.freshness === "stale"
                                  ? html`<div>
                                      ${
                                        typeof repo.behind_count === "number"
                                          ? i18n.t("sourceBehind", {
                                              count: repo.behind_count,
                                            })
                                          : i18n.t("sourceDistanceUnknown")
                                      }
                                    </div>`
                                  : null
                              }
                            </td>
                            <td>
                              ${dateTime(repo.last_attempt_at || repo.updated_at)}
                            </td>
                            <td>${this.nextUpdate(repo)}</td>
                            <td>
                              ${repo.last_result ? this.phase(repo.last_result) : "—"}
                            </td>
                            <td>
                              <code title=${repo.head || ""}
                                >${repo.head?.slice(0, 12) || "—"}</code
                              >
                            </td>
                            <td>${this.detailButton(repo)}</td>
                            <td>
                              ${
                                admin
                                  ? html`<a2k-button
                                        size="small"
                                        ?disabled=${this.busy}
                                        @click=${() => this.openEditor(repo)}
                                        >${i18n.t("edit")}</a2k-button
                                      >
                                      <a2k-button
                                        size="small"
                                        ?disabled=${this.busy || repo.updating}
                                        @click=${() => (this.deleteRepo = repo)}
                                        >${i18n.t("remove")}</a2k-button
                                      >`
                                  : null
                              }
                            </td>
                          </tr>`,
                      )}
                    </tbody>
                  </table>
                </div>
              </surface-window>
              <surface-window heading=${i18n.t("sourceHistory")}>
                <div class="toolbar source-history-toolbar">
                  <label class="native-field codex-history-page-size">
                    <span>${i18n.t("codexHistoryPageSize")}</span>
                    <select
                      class="a2k-native-input"
                      .value=${String(this.pageSize)}
                      @change=${(event: Event) => {
                        this.pageSize = Number(
                          (event.target as HTMLSelectElement).value,
                        );
                        this.page = 1;
                        void this.load();
                      }}
                    >
                      ${[10, 20, 50, 100].map((size) => html`<option value=${size}>${size}</option>`)}
                    </select>
                  </label>
                </div>
                <div class="table-wrap">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>${i18n.t("sourceStart")}</th>
                        <th>${i18n.t("sourceEnd")}</th>
                        <th>${i18n.t("sourceDuration")}</th>
                        <th>${i18n.t("sourceTrigger")}</th>
                        <th>${i18n.t("state")}</th>
                        <th>${i18n.t("sourceSuccess")}</th>
                        <th>${i18n.t("sourceFailed")}</th>
                        <th>${i18n.t("sourceExecuting")}</th>
                        <th>${i18n.t("sourceTotal")}</th>
                        <th class="source-actions-column">
                          ${i18n.t("actions")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      ${history.map(
                        (run) =>
                          html`<tr>
                            <td>${dateTime(run.started_at)}</td>
                            <td>${dateTime(run.finished_at)}</td>
                            <td>
                              ${this.duration(run.started_at, run.finished_at)}
                            </td>
                            <td>${this.triggerLabel(run.trigger)}</td>
                            <td>${this.phase(run.status)}</td>
                            <td>${run.success_count}</td>
                            <td>${run.failure_count}</td>
                            <td>${run.execution_count}</td>
                            <td>${run.total_count}</td>
                            <td>
                              <div class="source-row-actions">
                                <a2k-button
                                  size="small"
                                  @click=${() => (this.detailId = run.id)}
                                  >${i18n.t("details")}</a2k-button
                                >
                                ${
                                  run.status === "running" &&
                                  session.user?.role !== "viewer"
                                    ? html`<a2k-button
                                        size="small"
                                        ?disabled=${this.busy || run.cancel_requested}
                                        @click=${() => this.stopRun(run.id)}
                                        >${i18n.t(run.cancel_requested ? "sourceStopping" : "sourceStop")}</a2k-button
                                      >`
                                    : null
                                }
                              </div>
                            </td>
                          </tr>`,
                      )}
                    </tbody>
                  </table>
                </div>
                ${!history.length ? html`<p>${i18n.t("sourceEmpty")}</p>` : null}
                <app-pagination
                  .page=${this.page}
                  .totalPages=${pageCount(this.data?.history_count || 0, this.pageSize)}
                  @page-change=${(event: CustomEvent<{ page: number }>) => {
                    this.page = event.detail.page;
                    void this.load();
                  }}
                ></app-pagination>
              </surface-window>
            `
      }
      <app-dialog
        .open=${Boolean(detail)}
        heading=${i18n.t("sourceBatch")}
        cancel-label=${i18n.t("close")}
        ?hide-confirm=${true}
        @dialog-cancel=${() => (this.detailId = "")}
      >
        ${
          detail
            ? html`<p>
                  ${dateTime(detail.started_at)} · ${this.phase(detail.status)}
                  · ${detail.success_count}/${detail.total_count}
                  ${i18n.t("sourceSuccess")} · ${detail.failure_count}
                  ${i18n.t("sourceFailed")}
                </p>
                <div class="table-wrap source-detail-table">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>${i18n.t("name")}</th>
                        <th>${i18n.t("sourceStart")}</th>
                        <th>${i18n.t("sourceEnd")}</th>
                        <th>${i18n.t("sourceDuration")}</th>
                        <th>${i18n.t("state")}</th>
                        <th>${i18n.t("sourceCommit")}</th>
                        <th>${i18n.t("details")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${Object.values(detail.repositories).map(
                        (repo) =>
                          html`<tr>
                            <td>${repo.name}</td>
                            <td>${dateTime(repo.started_at)}</td>
                            <td>${dateTime(repo.finished_at)}</td>
                            <td>
                              ${this.duration(repo.started_at, repo.finished_at)}
                            </td>
                            <td>${this.phase(repo.phase)}</td>
                            <td><code>${repo.head || "—"}</code></td>
                            <td>${this.detailButton(repo, detail.id)}</td>
                          </tr>`,
                      )}
                    </tbody>
                  </table>
                </div>`
            : null
        }
      </app-dialog>
      <knowledge-update-dialog
        .open=${this.updateOpen}
        .repositories=${this.updateRepositories}
        @update-close=${() => {
          this.updateOpen = false;
        }}
        @update-completed=${() => void this.load()}
      ></knowledge-update-dialog>
      <app-dialog
        .open=${Boolean(this.editor)}
        heading=${i18n.t(this.editName ? "sourceEdit" : "sourceAdd")}
        cancel-label=${i18n.t("cancel")}
        confirm-label=${i18n.t("save")}
        .busy=${this.busy}
        @dialog-confirm=${this.saveRepo}
        @dialog-cancel=${() => (this.editor = null)}
      >
        ${
          this.editor
            ? html`<div class="form-grid">
                ${(["name", "url", "branch"] as const).map(
                  (key) =>
                    html`<label class="native-field">
                      <span
                        >${i18n.t(key === "url" ? "sourceUrl" : key === "branch" ? "sourceBranch" : "name")}</span
                      >
                      <input
                        class="a2k-native-input"
                        .value=${this.editor![key]}
                        ?disabled=${this.busy || (key === "name" && Boolean(this.editName))}
                        @input=${(event: Event) => {
                          this.editor = {
                            ...this.editor!,
                            [key]: (event.target as HTMLInputElement).value,
                          };
                        }}
                      />
                    </label>`,
                )}
                ${(["auto_refresh", "copy_enabled"] as const).map(
                  (key) =>
                    html`<label class="native-field">
                      <input
                        type="checkbox"
                        .checked=${this.editor![key]}
                        ?disabled=${this.busy}
                        @change=${(event: Event) => {
                          this.editor = {
                            ...this.editor!,
                            [key]: (event.target as HTMLInputElement).checked,
                          };
                        }}
                      />
                      <span
                        >${i18n.t(key === "auto_refresh" ? "sourceAuto" : "sourceCopy")}</span
                      >
                    </label>`,
                )}
              </div>`
            : null
        }
        ${this.actionError ? html`<p class="status status-error">${this.actionError}</p>` : null}
      </app-dialog>
      <app-dialog
        .open=${Boolean(this.deleteRepo)}
        heading=${i18n.t("remove")}
        cancel-label=${i18n.t("cancel")}
        message=${i18n.t("sourceDelete")}
        confirm-label=${i18n.t("remove")}
        .busy=${this.busy}
        @dialog-confirm=${this.removeRepo}
        @dialog-cancel=${() => (this.deleteRepo = null)}
      >
        <p>${this.deleteRepo?.name}</p>
        ${this.actionError ? html`<p class="status status-error">${this.actionError}</p>` : null}
      </app-dialog>
      <app-dialog
        .open=${Boolean(this.errorRepo)}
        heading=${this.errorRepo?.name || i18n.t("details")}
        cancel-label=${i18n.t("close")}
        ?hide-confirm=${true}
        @dialog-cancel=${() => (this.errorRepoName = "")}
      >
        ${
          this.errorRepo
            ? html`<p>
                  ${this.phase(this.errorRepo.phase)} ·
                  ${dateTime(this.errorRepo.started_at)} →
                  ${dateTime(this.errorRepo.finished_at)}
                </p>
                <p><code>${this.errorRepo.head || "—"}</code></p>
                <p>
                  ${i18n.t("sourceRemote")}:
                  <code>${this.errorRepo.remote_head || "—"}</code>
                </p>
                <p>
                  ${i18n.t("sourceRemoteChecked")}:
                  ${dateTime(this.errorRepo.remote_checked_at)}
                </p>
                ${
                  this.errorRepo.remote_error
                    ? html`<p class="status status-error">
                        ${this.errorRepo.remote_error}
                      </p>`
                    : null
                }
                ${
                  this.errorRepo.error ||
                  this.errorRepo.attempt_errors?.length ||
                  this.errorRepo.filesystem_omissions?.length
                    ? html`<h3>${i18n.t("sourceError")}</h3>
                        ${this.errors(this.errorRepo)}`
                    : null
                }
                ${this.errorRepo.comparison_error ? html`<p>${this.errorRepo.comparison_error}</p>` : null}
                <h3>${i18n.t("sourceProgress")}</h3>
                <p>
                  ${this.duration(this.errorRepo.started_at, this.errorRepo.finished_at)}
                  · ${this.errorRepo.progress_operation || "—"}
                </p>
                <pre class="codex-history-output">
${
                    (this.errorRepo.progress_events || [])
                      .map((event) => `${dateTime(event.at)} ${event.message}`)
                      .join("\n") ||
                    this.errorRepo.progress_message ||
                    (this.errorRepo.updating
                      ? i18n.t("sourceProgressWaiting")
                      : "—")
                  }</pre> `
            : null
        }
      </app-dialog>
    `;
  }
}
